/**
 * @description AuthLayout - Dual-mode authentication wrapper with tenant branding
 *
 * Key features:
 * - Dual-mode layout:
 *   - LynxPay (isDefault): Premium split marketing layout with features grid
 *   - Tenants (!isDefault): Clean simple layout with nav bar + centered form
 * - Dynamic brand accent colors via CSS custom properties
 * - Framer Motion animations for smooth transitions
 * - Responsive: LynxPay mobile uses the App auth header; no second wordmark in the form column
 * - Tenant logo display lives in AuthNav (image or text fallback)
 *
 * @dependencies
 * - framer-motion: Page animations
 * - lucide-react: Icons
 * - BrandingContext: Tenant branding config (isDefault, branding)
 *
 * @notes
 * - PCI DSS compliant presentation
 * - WCAG 2.1 AA accessible
 * - Brand accent color comes from CSS custom properties (--brand-primary)
 *   set by BrandingContext, consumed via Tailwind `brand` color classes
 * - Tenants get the simple layout by default; marketing layout is LynxPay-specific
 * - Auth-specific colors (auth-bg, auth-surface) defined in tailwind.config.js
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBranding } from '../../contexts/BrandingContext';
import AuthNav from './AuthNav';

const AUTH_TITLES = {
  '/sign-in': 'Sign In',
  '/sign-up': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/accept-invite': 'Accept Invite',
  '/accept-company-invite': 'Company Invitation',
  '/auth/callback': 'Signing In...',
};

/**
 * Glassmorphism form card with animated content transitions.
 * Shared between LynxPay and tenant layouts.
 */
function AuthFormCard({ children, locationKey }) {
  return (
    <div className="bg-auth-surface/80 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-10 sm:p-12 shadow-[0_40px_80px_-15px_rgba(0,0,0,0.7)]">
      <AnimatePresence mode="wait">
        <motion.div
          key={locationKey}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/**
 * Animated helper text below the form card (e.g., "Don't have an account? Create one").
 */
function AuthFormHelper({ helper }) {
  if (!helper) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4 }}
      className="mt-10 text-center"
    >
      {helper}
    </motion.div>
  );
}

function AuthLayout({
  helper,
  children,
  title = <>Scale your business<br /><span className="text-brand">without limits.</span></>,
  subtitle = "The direct-to-merchant experience designed for growth. Replace fragmented tools with a single, unified platform.",
  features = []
}) {
  const { branding, isDefault } = useBranding();
  const { platformName, tagline } = branding.identity;
  const location = useLocation();

  const brandName = branding.identity.documentTitle || platformName;

  useEffect(() => {
    const matchedKey = Object.keys(AUTH_TITLES).find(key => location.pathname.startsWith(key));
    const pageTitle = matchedKey ? AUTH_TITLES[matchedKey] : 'Sign In';
    document.title = `${pageTitle} | ${brandName}`;
  }, [location.pathname, brandName]);

  // Tenant simple layout (non-LynxPay)
  if (!isDefault) {
    return (
      <div className="min-h-screen flex flex-col bg-auth-bg text-white font-sans overflow-x-hidden">
        <AuthNav />

        {/* Centered Form */}
        <div className="flex-1 flex flex-col justify-center items-center pt-24 pb-10 px-4 sm:px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-[420px] relative z-10"
          >
            <AuthFormCard locationKey={location.pathname}>
              {children}
            </AuthFormCard>
            <AuthFormHelper helper={helper} />
          </motion.div>
        </div>
      </div>
    );
  }

  // LynxPay marketing layout (default)
  return (
    <div className="min-h-screen bg-auth-bg text-white font-sans selection:bg-brand/30 overflow-x-hidden">
      {/* Global Background Elements */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand/[0.03] blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand/[0.02] blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 lg:grid lg:grid-cols-2 min-h-screen">

        {/* Left Column: Premium Marketing */}
        <div className="hidden lg:flex flex-col justify-between p-12 xl:p-20 border-r border-white/5 bg-black/20 backdrop-blur-3xl">
          <div className="max-w-xl">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/20 text-brand text-[10px] font-bold tracking-widest uppercase mb-8">
                <Sparkles className="w-3 h-3" />
                {tagline || platformName}
              </div>

              <h2 className="text-5xl xl:text-6xl font-bold tracking-tight leading-[1.1] mb-8">{title}</h2>

              <p className="text-xl text-gray-400 mb-12 leading-relaxed font-medium">
                {subtitle}
              </p>
            </motion.div>

            {features.length > 0 && (
              <div className="grid grid-cols-2 gap-8">
                {features.map(({ icon: Icon, title: featureTitle, description }, index) => (
                  <motion.div
                    key={featureTitle}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + (index * 0.1) }}
                    className="group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 group-hover:border-brand/30 group-hover:bg-brand/5 transition-all">
                      <Icon className="w-6 h-6 text-brand" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-brand transition-colors">{featureTitle}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex items-center gap-10 pt-12 border-t border-white/5"
          >
            <div className="flex items-center gap-3 text-[11px] text-gray-500 font-bold uppercase tracking-[0.2em]">
              <ShieldCheck className="w-4 h-4 text-brand" />
              PCI DSS LEVEL 1
            </div>
            <div className="flex items-center gap-3 text-[11px] text-gray-500 font-bold uppercase tracking-[0.2em]">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              SYSTEMS OPERATIONAL
            </div>
          </motion.div>
        </div>

        {/* Right Column: Centered Auth Form */}
        <div className="flex flex-col justify-center items-center min-h-screen p-6 sm:p-10 lg:p-20 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-[420px] relative z-10"
          >
            <AuthFormCard locationKey={location.pathname}>
              {children}
            </AuthFormCard>
            <AuthFormHelper helper={helper} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
